import { smoothRingPath } from './curves';

/** A deterministic, browser-independent color-region tracer. All coordinates use the working raster. */
export type VectorizeSettings = {
  mode: 'color' | 'black';
  colors: number;
  threshold: number;
  detail: number;
  smoothing: number;
  minArea: number;
  ignoreWhite: boolean;
};
export const defaultVectorizeSettings: VectorizeSettings = {
  // The worker routes smoothing > 0 through source-subpixel black tracing or
  // Smooth mode uses the dedicated subpixel black / shared-boundary color engines.
  // The contour engine in this file remains the pixel fallback.
  mode: 'color', colors: 12, threshold: 128, detail: 85, smoothing: 65, minArea: 0, ignoreWhite: false,
};
export type VectorPath = { id: string; fill: string; d: string; area: number; fillRule?: 'evenodd' | 'nonzero' };
export type VectorizeResult = {
  /** pointCount includes serialized Bézier handles, not just editable anchor nodes. */
  svg: string; width: number; height: number; paths: VectorPath[]; palette: string[]; pointCount: number;
  /** Connected regions before seam-free ordered color compositing. */
  regionCount?: number;
};
type PixelImage = { width: number; height: number; data: Uint8ClampedArray };
type Point = { x: number; y: number };
type Color = { r: number; g: number; b: number; weight: number; bin: number };
const MAX_PIXELS = 1_500_000;
const MAX_EDGES = 1_000_000;
const MAX_PATHS = 12_000;
const MAX_SVG_BYTES = 16_000_000;
const complexityError = () => new Error('Ảnh có quá nhiều chi tiết. Hãy giảm độ phân giải, số màu hoặc tăng lọc nhiễu.');

function validate(image: PixelImage, settings: VectorizeSettings) {
  const { width, height, data } = image;
  if (!Number.isInteger(width) || !Number.isInteger(height) || width < 1 || height < 1 || width * height > MAX_PIXELS) {
    throw new Error('Ảnh xử lý phải có kích thước hợp lệ và không quá 1,5 megapixel.');
  }
  if (!(data instanceof Uint8ClampedArray) || data.length !== width * height * 4) throw new Error('Dữ liệu pixel ảnh không hợp lệ.');
  if (settings.mode !== 'color' && settings.mode !== 'black') throw new Error('Chế độ vector không hợp lệ.');
  const ranges: [number, number, number][] = [[settings.colors, 2, 32], [settings.threshold, 0, 255], [settings.detail, 0, 100], [settings.smoothing, 0, 100], [settings.minArea, 0, 1000]];
  if (ranges.some(([value, min, max]) => !Number.isFinite(value) || value < min || value > max) || !Number.isInteger(settings.colors) || typeof settings.ignoreWhite !== 'boolean') throw new Error('Thông số tách vector không hợp lệ.');
}

const colorHex = ({ r, g, b }: Pick<Color, 'r' | 'g' | 'b'>) => '#' + [r, g, b].map(value => Math.round(value).toString(16).padStart(2, '0')).join('');
const binOf = (r: number, g: number, b: number) => ((r >> 3) << 10) | ((g >> 3) << 5) | (b >> 3);

/** Weighted median cut avoids a per-pixel iterative clustering cost on large uploads. */
function colorLabels(data: Uint8ClampedArray, settings: VectorizeSettings): { labels: Int16Array; fills: string[] } {
  const labels = new Int16Array(data.length / 4).fill(-1);
  if (settings.mode === 'black') {
    for (let pixel = 0; pixel < labels.length; pixel++) {
      const offset = pixel * 4;
      if (data[offset + 3] >= 128 && 0.2126 * data[offset] + 0.7152 * data[offset + 1] + 0.0722 * data[offset + 2] <= settings.threshold) labels[pixel] = 0;
    }
    return { labels, fills: ['#000000'] };
  }
  const weights = new Uint32Array(32768);
  const sums = new Float64Array(32768 * 3);
  for (let offset = 0; offset < data.length; offset += 4) {
    if (data[offset + 3] < 128) continue;
    const r = data[offset], g = data[offset + 1], b = data[offset + 2];
    if (settings.ignoreWhite && r >= 245 && g >= 245 && b >= 245) continue;
    const bin = binOf(r, g, b);
    weights[bin]++;
    sums[bin * 3] += r; sums[bin * 3 + 1] += g; sums[bin * 3 + 2] += b;
  }
  const colors: Color[] = [];
  for (let bin = 0; bin < weights.length; bin++) {
    const weight = weights[bin];
    if (weight) colors.push({ r: sums[bin * 3] / weight, g: sums[bin * 3 + 1] / weight, b: sums[bin * 3 + 2] / weight, weight, bin });
  }
  if (!colors.length) return { labels, fills: [] };
  const buckets = [colors];
  const spread = (bucket: Color[]) => {
    let minR = 255, minG = 255, minB = 255, maxR = 0, maxG = 0, maxB = 0, weight = 0;
    for (const color of bucket) {
      minR = Math.min(minR, color.r); minG = Math.min(minG, color.g); minB = Math.min(minB, color.b);
      maxR = Math.max(maxR, color.r); maxG = Math.max(maxG, color.g); maxB = Math.max(maxB, color.b); weight += color.weight;
    }
    const ranges = [maxR - minR, maxG - minG, maxB - minB];
    const axis = ['r', 'g', 'b'][ranges.indexOf(Math.max(...ranges))] as 'r' | 'g' | 'b';
    return { axis, weight, score: Math.max(...ranges) * Math.sqrt(weight) };
  };
  while (buckets.length < settings.colors) {
    let index = -1, best = -1;
    for (let i = 0; i < buckets.length; i++) {
      if (buckets[i].length < 2) continue;
      const score = spread(buckets[i]).score;
      if (score > best) { best = score; index = i; }
    }
    if (index < 0) break;
    const bucket = buckets[index], { axis, weight } = spread(bucket);
    bucket.sort((a, b) => a[axis] - b[axis] || a.bin - b.bin);
    let accumulated = 0, split = 0;
    while (split < bucket.length - 1 && accumulated < weight / 2) accumulated += bucket[split++].weight;
    split = Math.max(1, split);
    buckets.splice(index, 1, bucket.slice(0, split), bucket.slice(split));
  }
  const binLabels = new Int16Array(32768).fill(-1);
  const fills = buckets.map((bucket, label) => {
    let r = 0, g = 0, b = 0, weight = 0;
    for (const color of bucket) {
      r += color.r * color.weight; g += color.g * color.weight; b += color.b * color.weight; weight += color.weight;
      binLabels[color.bin] = label;
    }
    return colorHex({ r: r / weight, g: g / weight, b: b / weight });
  });
  for (let pixel = 0; pixel < labels.length; pixel++) {
    const offset = pixel * 4;
    if (data[offset + 3] >= 128 && !(settings.ignoreWhite && data[offset] >= 245 && data[offset + 1] >= 245 && data[offset + 2] >= 245)) labels[pixel] = binLabels[binOf(data[offset], data[offset + 1], data[offset + 2])];
  }
  return { labels, fills };
}

const squaredDistance = (a: Point, b: Point) => (a.x - b.x) ** 2 + (a.y - b.y) ** 2;
function segmentDistance(point: Point, a: Point, b: Point) {
  const length = squaredDistance(a, b);
  if (!length) return squaredDistance(point, a);
  const t = Math.max(0, Math.min(1, ((point.x - a.x) * (b.x - a.x) + (point.y - a.y) * (b.y - a.y)) / length));
  return squaredDistance(point, { x: a.x + t * (b.x - a.x), y: a.y + t * (b.y - a.y) });
}

/** Iterative RDP keeps adversarial zigzags away from the JavaScript recursion limit. */
function simplifyOpen(points: Point[], tolerance: number) {
  if (points.length < 3) return points;
  const keep = new Uint8Array(points.length); keep[0] = keep[points.length - 1] = 1;
  const stack = [0, points.length - 1];
  let comparisons = 0;
  while (stack.length) {
    const end = stack.pop()!, start = stack.pop()!;
    let farthest = -1, maximum = tolerance * tolerance;
    for (let i = start + 1; i < end; i++) {
      if (++comparisons > 4_000_000) return points; // Exact geometry is a safe bounded-cost fallback.
      const distance = segmentDistance(points[i], points[start], points[end]);
      if (distance > maximum) { maximum = distance; farthest = i; }
    }
    if (farthest >= 0) { keep[farthest] = 1; stack.push(start, farthest, farthest, end); }
  }
  return points.filter((_, index) => keep[index]);
}

function simplifyRing(points: Point[], tolerance: number) {
  const corners = points.filter((point, i) => {
    const previous = points[(i + points.length - 1) % points.length], next = points[(i + 1) % points.length];
    return (point.x - previous.x) * (next.y - point.y) !== (point.y - previous.y) * (next.x - point.x);
  });
  if (!tolerance || corners.length <= 4) return corners;
  let opposite = 1;
  for (let i = 2; i < corners.length; i++) if (squaredDistance(corners[0], corners[i]) > squaredDistance(corners[0], corners[opposite])) opposite = i;
  const simplified = [...simplifyOpen(corners.slice(0, opposite + 1), tolerance).slice(0, -1), ...simplifyOpen([...corners.slice(opposite), corners[0]], tolerance).slice(0, -1)];
  return simplified.length >= 3 ? simplified : corners;
}

const coordinate = (value: number) => String(Math.round(value * 100) / 100);
const pair = (point: Point) => `${coordinate(point.x)} ${coordinate(point.y)}`;
function ringPath(points: Point[]): { d: string; points: number } {
  return { d: `M${pair(points[0])}${points.slice(1).map(point => `L${pair(point)}`).join('')}Z`, points: points.length };
}

/** Edges run clockwise around foreground; a right turn keeps touching diagonal regions apart. */
function boundaryRings(edges: Set<number>, stride: number): Point[][] {
  const rings: Point[][] = [];
  const deltas = [1, stride, -1, -stride];
  while (edges.size) {
    const first = edges.values().next().value as number;
    let edge = first;
    const points: Point[] = [];
    do {
      if (!edges.delete(edge)) throw new Error('Không khép kín được đường viền ảnh.');
      const vertex = Math.floor(edge / 4), direction = edge % 4;
      points.push({ x: vertex % stride, y: Math.floor(vertex / stride) });
      const end = vertex + deltas[direction];
      if (end === Math.floor(first / 4)) break;
      let next = -1;
      for (const turn of [1, 0, 3, 2]) {
        const candidate = end * 4 + (direction + turn) % 4;
        if (edges.has(candidate)) { next = candidate; break; }
      }
      if (next < 0) throw new Error('Không khép kín được đường viền ảnh.');
      edge = next;
    } while (edge !== first);
    if (points.length >= 4) rings.push(points);
  }
  return rings;
}

/** Builds editable compound SVG paths; transparent pixels and interior holes stay transparent. */
export function vectorizeImage(image: PixelImage, settings: VectorizeSettings): VectorizeResult {
  validate(image, settings);
  const { width, height } = image, { labels, fills } = colorLabels(image.data, settings);
  const visited = new Uint8Array(width * height), queue = new Int32Array(width * height);
  const paths: VectorPath[] = [];
  let edgeCount = 0, pointCount = 0, svgLength = 0;
  const tolerance = (100 - settings.detail) / 100 * 3;
  for (let start = 0; start < labels.length; start++) {
    const label = labels[start];
    if (label < 0 || visited[start]) continue;
    let tail = 1, head = 0;
    queue[0] = start; visited[start] = 1;
    while (head < tail) {
      const pixel = queue[head++], x = pixel % width;
      // Four-connected regions ensure diagonally touching objects remain separately editable.
      for (const neighbor of [x ? pixel - 1 : -1, x + 1 < width ? pixel + 1 : -1, pixel - width, pixel + width]) {
        if (neighbor >= 0 && neighbor < labels.length && !visited[neighbor] && labels[neighbor] === label) { visited[neighbor] = 1; queue[tail++] = neighbor; }
      }
    }
    if (tail < settings.minArea) continue;
    if (paths.length >= MAX_PATHS) throw complexityError();
    const edges = new Set<number>(), stride = width + 1;
    const addEdge = (edge: number) => {
      if (edgeCount + edges.size >= MAX_EDGES) throw complexityError();
      edges.add(edge);
    };
    for (let i = 0; i < tail; i++) {
      const pixel = queue[i], x = pixel % width, y = Math.floor(pixel / width), vertex = y * stride + x;
      if (y === 0 || labels[pixel - width] !== label) addEdge(vertex * 4);
      if (x === width - 1 || labels[pixel + 1] !== label) addEdge((vertex + 1) * 4 + 1);
      if (y === height - 1 || labels[pixel + width] !== label) addEdge((vertex + stride + 1) * 4 + 2);
      if (x === 0 || labels[pixel - 1] !== label) addEdge((vertex + stride) * 4 + 3);
    }
    edgeCount += edges.size;
    if (edgeCount > MAX_EDGES) throw complexityError();
    const rings = boundaryRings(edges, stride);
    let d = '';
    for (const ring of rings) {
      const junction = (point: Point) => {
        const neighboring = new Set<number>();
        for (const y of [point.y - 1, point.y]) for (const x of [point.x - 1, point.x]) neighboring.add(x >= 0 && x < width && y >= 0 && y < height ? labels[y * width + x] : -1);
        return neighboring.size >= 3;
      };
      const narrow = (point: Point, tangent: Point) => {
        const length = Math.hypot(tangent.x, tangent.y);
        if (!length) return true;
        const nx = -tangent.y / length, ny = tangent.x / length;
        const sample = (distance: number) => {
          const x = Math.floor(point.x + nx * distance), y = Math.floor(point.y + ny * distance);
          return x >= 0 && x < width && y >= 0 && y < height ? labels[y * width + x] : -1;
        };
        for (const sign of [-1, 1]) {
          const initial = sample(sign * .65);
          // Constrain the negative-space/color corridor outside this region.
          // The other side can cross a small antialias stair inside a broad
          // region; treating that as a thin gap prevents useful smoothing.
          if (initial === label) continue;
          if (initial !== sample(sign * 1.05)) continue;
          for (let distance = 1.5; distance <= 3.5; distance += .5) if (sample(sign * distance) !== initial) return true;
        }
        return false;
      };
      const path = settings.smoothing ? smoothRingPath(ring, settings.smoothing, settings.detail, junction, narrow) : ringPath(simplifyRing(ring, tolerance));
      d += path.d; pointCount += path.points;
    }
    svgLength += d.length;
    if (svgLength > MAX_SVG_BYTES) throw complexityError();
    paths.push({ id: `path-${paths.length + 1}`, fill: fills[label], d, area: tail });
  }
  const palette = [...new Set(paths.map(path => path.fill))];
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}"><title>DesignForge — Image Trace Promax</title>${paths.map(path => `<path id="${path.id}" fill="${path.fill}" fill-rule="evenodd" d="${path.d}"/>`).join('')}</svg>`;
  return { svg, width, height, paths, palette, pointCount };
}
